// src/components/doctor/PatientSnapshot.jsx
//
// The at-a-glance strip on the doctor's patient page, above the tabs:
// risk trend, what's still open, and when the patient was last / is next seen.
// Everything here is derived from data DoctorPatientDetailPage already loads
// (plus the doctor's appointment list and signed visits), so it adds no new
// endpoints. Open items reads "1 urgent · 5 routine" (utils/actionItems.js);
// Last visit shows the diagnosis headline of the latest SIGNED visit and links
// to it in the Visits tab, falling back to the appointment list / lastVisitDate.

import React from 'react';
import { Link } from 'react-router-dom';
import RiskTrendCard from './RiskTrendCard';
import { groupOpenItems } from '../../utils/actionItems';
import { humanise } from '../../utils/appointmentDisplay';
import { toDay } from '../../utils/activeMedicines';

function fmtDay(value) {
  if (!value) return null;
  // A bare 'YYYY-MM-DD' is a calendar day: build it locally so the zone cannot shift it.
  const day = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? String(value).split('-').map(Number) : null;
  const d = day ? new Date(day[0], day[1] - 1, day[2]) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Tile({ label, children, sub, onClick, ariaLabel, urgent, textValue }) {
  const body = (
    <>
      <div className="stat-label">{label}</div>
      <div className={`stat-value${textValue ? ' stat-value--text' : ''}`}>{children}</div>
      {sub && <div className="stat-change">{sub}</div>}
    </>
  );
  if (!onClick) return <div className="stat-card">{body}</div>;
  // Clickable tile: a real button, so it is keyboard-reachable.
  return (
    <button type="button" className={`stat-card patient-snapshot-button${urgent ? ' prec-tile--urgent' : ''}`} onClick={onClick} aria-label={ariaLabel}>
      {body}
    </button>
  );
}

export default function PatientSnapshot({
  reports = [], doctorItems = [], lifestyleItems = [], appointments = [],
  lastSignedVisit = null, fallbackLastVisitDate = null, onOpenVisit,
  onOpenItems, onOpenReports, itemsStatus = 'ready',
}) {
  const openItems = doctorItems.filter(i => !i.completed);
  const { now: urgentItems, routine: routineItems } = groupOpenItems(doctorItems);
  // A call that has not answered, or failed, is not "nothing open": say so instead of "0 / All caught up".
  const itemsUnknown = itemsStatus === 'loading' || itemsStatus === 'failed';
  const itemsText = itemsUnknown ? (itemsStatus === 'failed' ? 'Couldn’t load' : 'Loading…')
    : urgentItems.length > 0
    ? `${urgentItems.length} urgent${routineItems.length ? ` · ${routineItems.length} routine` : ''}`
    : routineItems.length > 0 ? `${routineItems.length} routine` : '0';
  // Calendar days compared as local 'YYYY-MM-DD' strings (a bare due date parsed as UTC midnight shifts a day in some zones).
  const todayLocal = toDay(new Date());
  const overdue = openItems.filter(i => i.dueDate && String(i.dueDate).slice(0, 10) < todayLocal);
  const pendingReview = lifestyleItems.filter(i => i.status === 'PENDING_REVIEW');

  const todayIso = todayLocal; // the doctor's local day (toISOString() is the UTC day: wrong for 5.5h a day in IST)
  const live = appointments.filter(a => a.status !== 'CANCELLED');
  const last = live
    .filter(a => a.appointmentDate && a.appointmentDate < todayIso && a.status !== 'NO_SHOW')
    .sort((a, b) => b.appointmentDate.localeCompare(a.appointmentDate))[0];
  const next = live
    .filter(a => a.appointmentDate && a.appointmentDate >= todayIso
      && a.status !== 'COMPLETED' && a.status !== 'NO_SHOW')
    .sort((a, b) => a.appointmentDate.localeCompare(b.appointmentDate))[0];

  return (
    <div className="stats-row patient-snapshot fade-in-up delay-1">
      <RiskTrendCard reports={reports} onOpen={onOpenReports} />

      <Tile label="Open items"
        onClick={itemsUnknown ? undefined : onOpenItems}
        urgent={!itemsUnknown && urgentItems.length > 0}
        textValue
        ariaLabel={`Open items: ${itemsText}. Show the list`}
        sub={itemsUnknown ? (itemsStatus === 'failed' ? 'See the notice above to retry' : ' ')
          : overdue.length > 0 ? `${overdue.length} overdue · Review →`
          : openItems.length > 0 ? 'Review →'
          : pendingReview.length > 0 ? `${pendingReview.length} lifestyle suggestion${pendingReview.length === 1 ? '' : 's'} to review`
          : 'All caught up'}>
        {itemsText}
      </Tile>

      {lastSignedVisit ? (
        <Tile label="Last visit"
          sub={(
            <>
              <span className="prec-clamp" title={lastSignedVisit.headline || undefined}>
                {lastSignedVisit.headline || 'Diagnosis not recorded'}
              </span>
              {onOpenVisit && (
                <button type="button" className="consult-link" onClick={() => onOpenVisit(lastSignedVisit.id)}>
                  Open →
                </button>
              )}
            </>
          )}>
          {fmtDay(lastSignedVisit.day) || '—'}
        </Tile>
      ) : (
        <Tile label="Last visit"
          sub={last ? (humanise(last.appointmentType) !== '—' ? humanise(last.appointmentType) : 'Consultation')
            : fallbackLastVisitDate ? 'Last recorded visit date' : 'No completed visit on record'}>
          {fmtDay(last?.appointmentDate || fallbackLastVisitDate) || '—'}
        </Tile>
      )}

      <Tile label="Next visit"
        sub={next ? `${next.appointmentTime ? String(next.appointmentTime).slice(0, 5) + ' · ' : ''}${next.appointmentType ? humanise(next.appointmentType) : 'Consultation'}` : 'Nothing scheduled'}>
        {next ? (
          <Link to={`/doctor/appointments/${next.id}`} style={{ color: 'inherit' }}>
            {fmtDay(next.appointmentDate)}
          </Link>
        ) : '—'}
      </Tile>
    </div>
  );
}
